import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, Briefcase, Users, Mail, FileText, Settings, LogOut, Menu, X, Sun, Moon } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/projects', label: 'Projects', icon: FolderKanban },
  { path: '/admin/services', label: 'Services', icon: Briefcase },
  { path: '/admin/careers', label: 'Careers', icon: Users },
  { path: '/admin/applications', label: 'Applications', icon: Users },
  { path: '/admin/contacts', label: 'Inquiries', icon: Mail },
  { path: '/admin/blogs', label: 'Blog', icon: FileText },
  { path: '/admin/settings', label: 'Settings', icon: Settings }
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
      <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-cyan-600 text-white rounded-lg">
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      <aside className={`fixed top-0 left-0 z-40 h-screen w-64 bg-gradient-to-b from-gray-800 to-gray-900 dark:from-gray-900 dark:to-black transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-gray-700/50">
          <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Algoryn Admin</h1>
        </div>
        <nav className="mt-6 px-4">
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink key={path} to={path} end={path === '/admin'} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${isActive ? 'bg-cyan-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>
              <Icon size={20} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full p-4 border-t border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold">{user?.name?.charAt(0)}</div>
            <div>
              <p className="text-sm text-white">{user?.name}</p>
              <p className="text-xs text-gray-400">{user?.role}</p>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-gray-300 hover:text-white w-full px-3 py-2 rounded-lg hover:bg-gray-700">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <div className="lg:ml-64 min-h-screen transition-colors duration-300">
        <header className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-md shadow-sm px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white capitalize">{location.pathname.split('/').pop() || 'Dashboard'}</h2>
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all shadow-sm"
            >
              {isDark ? <Sun size={20} className="text-amber-500" /> : <Moon size={20} className="text-gray-600" />}
            </button>
          </div>
        </header>
        <main className="p-6 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
          <Outlet />
        </main>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
}